import React, { useState, useEffect, useRef } from 'react';
import { Snackbar, Alert, Slide, Box, ClickAwayListener, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

interface NotificationProps {
  isLoading?: string;
  error?: string;
  delay?: number;
  autoHideDuration?: number;
  clearError: () => void;
}

export const Notification: React.FC<NotificationProps> = ({ 
  isLoading, 
  error, 
  delay = 300,
  autoHideDuration = 1200,
  clearError 
}) => {
  const [stableLoading, setStableLoading] = useState<string | undefined>(isLoading);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Immediately show loading
      setStableLoading(isLoading);
      // Clear any pending hide timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else {
      // Delay hiding the loading state
      timeoutRef.current = setTimeout(() => {
        setStableLoading(undefined);
        timeoutRef.current = null;
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, delay]);

  return (
    <>
      {/* Backdrop overlay when notification is shown */}
      {(stableLoading || error !== undefined) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'hsla(0, 33.30%, 95.90%, 0.05)',
            zIndex: (theme) => theme.zIndex.snackbar - 1,
            pointerEvents: stableLoading ? 'auto' : 'none'
          }}
        />
      )}
      
      {(stableLoading || error !== undefined) && (
        <Snackbar
          open={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slots={{ transition: Slide }}
          slotProps={{
            clickAwayListener: {
              onClickAway: () => {}, // Prevent closing on click away
            },
          }}
          autoHideDuration={autoHideDuration}
          style={{ bottom: '80px' }}
          sx={{
            zIndex: (theme) => theme.zIndex.snackbar,
          }}
        >
          {stableLoading ? (
            <Alert
              severity="info"
              variant="outlined"
              sx={{ 
                pr: 7,
                width: '100%',
                color: isLoading ? "warning.main" : error ? "error.main" : "success.main",
                borderColor: isLoading ? "warning.main" : error ? "error.main" : "success.main",
                backgroundColor: isLoading ? "warning.soft" : error ? "error.soft" : "success.soft",
                '& .MuiAlert-icon': {
                  color: isLoading ? "warning.main" : error ? "error.main" : "success.main"
                }
              }}
            >
              {isLoading ? isLoading : error ? error : 'done'}
            </Alert>
          ) : (
            <Alert
              severity="error"
              variant="outlined"
              sx={{ width: '100%',
                color: 'error.main',
                borderColor: 'error.main',
                backgroundColor: 'error.soft',
                '& .MuiAlert-icon': {
                  color: 'error.main'
                }
              }}
              onClose={clearError}
              action={
                <IconButton
                  aria-label="close"
                  color="error"
                  size="small"
                  onClick={clearError}
                >
                  <Close fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
        </Snackbar>
      )}
    </>
  );
};